import type {
	LoginFlow,
	UiNodeGroupEnum,
	UpdateLoginFlowBody,
} from "@ory/kratos-client";
import type { AxiosError } from "axios";
import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
	ContentContainer,
	ContentContainerWrapper,
} from "~/components/ContentContainer";
import { Flow, handleFlowError } from "~/components/KratosFlow";
import { DefaultLayout } from "~/components/Layout";
import { Loading } from "~/components/Loading";
import { useModal } from "~/components/Modal";
import { useSnackBar } from "~/components/SnackBar";
import { config } from "~/config";
import { kratos } from "~/ory";
import { Mfa } from "~/pages/Login/Mfa";
import { PasswordLogin } from "~/pages/Login/PasswordLogin";
import { ReAuthentication } from "~/pages/Login/ReAuthentication";
import { createFlowErrorHandler } from "~/utils/errorHandler";

export const Login: React.FC = () => {
	const [flow, setFlow] = useState<LoginFlow | undefined>();

	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [openModal, closeModal] = useModal();
	const [openSnackBar] = useSnackBar();

	const returnTo = searchParams.get("return_to") ?? config.tierivPortalUrl;
	const flowId = searchParams.get("flow");
	const refresh = searchParams.get("refresh");
	const aal = searchParams.get("aal");

	// biome-ignore lint/correctness/useExhaustiveDependencies: openSnackBar/openModal/closeModal are intentionally excluded. They are used only for fire-and-forget error display, and this effect should re-fetch on session/flow/URL changes only. They are stable in production (SnackBar/Modal providers memoize open/close via useCallback), so excluding them changes nothing at runtime; biome just cannot verify that stability across the context boundary.
	useEffect(() => {
		// If we already have a flow, do nothing.
		if (flow) {
			return;
		}

		// Fetching/initializing a flow can fail at the network level (request aborted,
		// offline) with no HTTP response. handleFlowError can't classify those and re-rejects,
		// so we add a terminal handler to avoid an unhandled promise rejection (mirrors submit()).
		const onFlowError = createFlowErrorHandler(
			openSnackBar,
			openModal,
			closeModal,
		);

		if (flowId) {
			kratos
				.getLoginFlow({ id: String(flowId) })
				.then(({ data }) => setFlow(data))
				.catch(handleFlowError(navigate, "login", setFlow))
				.catch(onFlowError);
			return;
		}
		kratos
			.createBrowserLoginFlow({
				refresh: Boolean(refresh),
				aal: aal ? String(aal) : undefined,
				returnTo,
			})
			.then(({ data }) => setFlow(data))
			.catch(handleFlowError(navigate, "login", setFlow))
			.catch(onFlowError);
	}, [flowId, aal, refresh, returnTo, flow, navigate]);

	const onSubmit = (values: UpdateLoginFlowBody) => {
		setSearchParams({ flow: String(flow?.id) });
		return (
			kratos
				.updateLoginFlow({
					flow: String(flow?.id),
					updateLoginFlowBody: values,
				})
				// We logged in successfully! Let's bring the user home.
				.then(({ data }) => {
					if (!data.session.identity) {
						kratos
							.createBrowserLoginFlow({
								aal: "aal2",
								returnTo: flow?.return_to ?? undefined,
							})
							.then(({ data }) => setFlow(data))
							.catch(
								createFlowErrorHandler(openSnackBar, openModal, closeModal),
							);
						return;
					}
					if (flow?.return_to) {
						window.location.href = flow?.return_to;
						return;
					}
					window.location.href = config.tierivPortalUrl;
				})
				.catch(handleFlowError(navigate, "login", setFlow))
				.catch((err: AxiosError<LoginFlow>) => {
					// If the previous handler did not catch the error it's most likely a form validation error
					if (err.response?.status === 400) {
						// Yup, it is!
						setFlow(err.response?.data);
						return;
					}

					createFlowErrorHandler(openSnackBar, openModal, closeModal)(err);
				})
		);
	};
	if (!flow) return <Loading />;

	const hasGroup = (flow: LoginFlow, group: UiNodeGroupEnum): boolean =>
		flow.ui.nodes.some((n) => n.group === group);
	const isInSession = flow.refresh && hasGroup(flow, "password");
	if (isInSession) {
		return <ReAuthentication flow={flow} onSubmit={onSubmit} />;
	}
	if (flow.requested_aal === "aal1") {
		return <PasswordLogin flow={flow} onSubmit={onSubmit} />;
	}
	if (flow.requested_aal === "aal2") {
		return <Mfa flow={flow} onSubmit={onSubmit} />;
	}

	// fallback
	return (
		<>
			<title>Login | TIER IV Account</title>
			<DefaultLayout>
				<ContentContainerWrapper>
					<ContentContainer>
						<Flow onSubmit={onSubmit} flow={flow} />
					</ContentContainer>
				</ContentContainerWrapper>
			</DefaultLayout>
		</>
	);
};
